import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import { useDashboard } from '../../../context/DashboardContext';
import { getCountries, useGeo } from '../../../utils/geo';
import { PARTICIPATION_BAND_COLORS } from '../constants';

import {BandChip} from '../../DashboardCommon/widgets.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';
import {SortToggle} from '../../DashboardCommon/SortToggle.comp';
import {sortItems} from '../../DashboardCommon/barSort';
const NAVY   = '#052F44';

const LevelChip = ({label}) => <BandChip label={label} color={PARTICIPATION_BAND_COLORS[label] || '#6B7280'}/>;

/* QA LA-PD-5 (9 Oct 2026): the country bars filter and sort. The search box
   matches the country name; the toggle orders the bars lowest first (the
   page's original order), highest first, or A–Z by country. */
const SORT_MODES = [
    {value: 'asc', label: 'Lowest first'},
    {value: 'desc', label: 'Highest first'},
    {value: 'group', label: 'A–Z'},
];

/* ── Horizontal bar ─────────────────────────────────────────────── */
/* Fill color matches the row's Very Low/Low/Medium/High tier chip via
   PARTICIPATION_BAND_COLORS, instead of a separate below/above-threshold
   red/blue scheme. */
const CoverageBar = ({pct, band}) => (
    <Box sx={{flex: 1, height: '22px', borderRadius: '4px', backgroundColor: 'var(--brand-surface-subtle, #E5E7EB)', overflow: 'hidden'}}>
        <Box sx={{
            width: `${pct}%`,
            height: '100%',
            backgroundColor: PARTICIPATION_BAND_COLORS[band] || '#6B7280',
            borderRadius: '4px',
            transition: 'width 0.4s ease',
        }}/>
    </Box>
);

const COVERAGE_OVERVIEW_SECTION_CODE = 'S_11';
const CARD_SECTION = COVERAGE_OVERVIEW_SECTION_CODE;

const CoverageOverviewTab = () => {
    useGeo(); // re-render with the API's lists once they load
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTION });
    const [query, setQuery] = useState('');
    const [sortMode, setSortMode] = useState('asc');

    const s11Section = useMemo(() => {
        return dashboardData?.cards?.sections?.find(
            (section) => section.section_code === COVERAGE_OVERVIEW_SECTION_CODE
        );
    }, [dashboardData]);

    const c2 = useMemo(() => {
        return s11Section?.cards?.find(c => c.card_code === 'C_2');
    }, [s11Section]);

    const countryMap = useMemo(() => {
        const map = {};
        getCountries().forEach(c => {
            map[c.code] = c.label;
        });
        return map;
    }, []);

    // One row per country, named for the search box and the A–Z order.
    const items = useMemo(() => (c2?.items || []).map((row) => ({...row, countryName: countryMap[row.label] || row.label})), [c2, countryMap]);

    const shownItems = useMemo(() => {
        const q = query.trim().toLowerCase();
        const matched = q ? items.filter((row) => String(row.countryName).toLowerCase().includes(q)) : items;
        if (sortMode === 'group') {
            return [...matched].sort((a, b) => String(a.countryName).localeCompare(String(b.countryName), undefined, {sensitivity: 'base'}));
        }
        return sortItems(matched, sortMode);
    }, [items, query, sortMode]);

    const threshold = useMemo(() => {
        const itemBenchmark = c2?.items?.[0]?.benchmark_values?.[0]?.value;
        if (typeof itemBenchmark === 'number') return itemBenchmark;
        return 75;
    }, [c2]);

    // Generic rule, not an enumerated country list — the deck states this as
    // one fixed sentence regardless of how many countries are below threshold.
    const guidanceText = useMemo(() => {
        return `Do not present countries' figures as representatives until coverage rises above ${threshold}%.`;
    }, [threshold]);

    if (loading) {
        return <CircularLoader />;
    }

    if (!s11Section) {
        return (
            <NoDataAvailable />
        );
    }

    return (
        <Box>
            <Box sx={{display: 'flex', gap: 2.5, alignItems: 'stretch'}}>

                {/* ── Left: Participation by country ── */}
                <Box sx={{
                    flex: 1,
                    border: '1px solid var(--brand-border, #E5E7EB)',
                    borderRadius: '12px',
                    backgroundColor: 'var(--brand-surface, #ffffff)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    p: 3,
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2}}>
                        <Typography sx={{fontSize: '18px', fontWeight: 800, color: NAVY}}>
                            {c2?.title || 'Participation by Country'}
                        </Typography>
                        <SortToggle value={sortMode} onChange={setSortMode} modes={SORT_MODES}/>
                    </Box>
                    <TableToolbar rows={shownItems} total={items.length} query={query} onQuery={setQuery} placeholder="Search by country…"/>

                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2.2}}>
                        {items.length > 0 && shownItems.length === 0 && (
                            <Typography sx={{fontSize: '13px', color: '#6A7282', textAlign: 'center', py: 2}}>No country matches the search.</Typography>
                        )}
                        {shownItems.map((row) => {
                            const pct = row.value ?? 0;
                            const countryName = row.countryName;
                            return (
                                <Box key={row.label} sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                    {/* Country name */}
                                    <Typography sx={{
                                        fontSize: '14px',
                                        color: '#374151',
                                        minWidth: '130px',
                                        flexShrink: 0,
                                    }}>
                                        {countryName}
                                    </Typography>
                                    {/* Bar */}
                                    <CoverageBar pct={pct} band={row.band}/>
                                    {/* Percentage */}
                                    <Typography sx={{
                                        fontSize: '15px',
                                        fontWeight: 700,
                                        color: NAVY,
                                        minWidth: '42px',
                                        textAlign: 'right',
                                        flexShrink: 0,
                                    }}>
                                        {pct}%
                                    </Typography>
                                    {/* Level chip */}
                                    <Box sx={{width: '95px', flexShrink: 0}}>
                                        <LevelChip label={row.band}/>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                    {/*<Typography sx={{fontSize: '12px', color: '#9CA3AF', fontStyle: 'italic', mt: 3, lineHeight: 1.6}}>*/}
                    {/*    Bar color reflects each country's Very Low/Low/Medium/High coverage tier. {threshold}% is the reporting threshold.*/}
                    {/*</Typography>*/}
                </Box>

            </Box>

            {/* Decision guidance footer */}
            <Box sx={{
                mt: 3,
                p: {xs: 2, md: 2.5},
                backgroundColor: 'var(--brand-surface-subtle, #EFF6FF)',
                border: '1px solid #BFDBFE',
                borderLeft: `4px solid ${NAVY}`,
                borderRadius: '0 8px 8px 0',
            }}>
                <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.7}}>
                    <strong style={{color: NAVY}}>Decision guidance:&nbsp;</strong>
                    {guidanceText}
                </Typography>
            </Box>
        </Box>
    );
};

export default CoverageOverviewTab;
